/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Stepper Shell
 * Page shell with stepper header. Non-dialog equivalent of StepperModal —
 * parent controls step content via render prop (children function).
 *
 * @file StepperShell.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button } from '@mui/material';
import DashboardCard from '../dashboardCard/DashboardCard';
import QuickScheduleStepper from '../QuickSchedule/QuickScheduleStepper';
import {
  StepperShellStylesRoot,
  StepperShellDashboardCardRoot,
  StepperShellDashboardCardActionsLeft,
  StepperShellDashboardCardActionsRight,
  StepperShellDashboardCardHeader,
  classes
} from './styles/StepperShellStyles';

const StepperShell = ({
  title,
  steps,
  activeStepId,
  completedStepIds = [],
  onStepChange,
  onStepComplete,
  onBackNavClick,
  disclaimer,
  footerActionsStepId,
  cancelButtonLabel,
  finishButtonLabel,
  onFooterCancel,
  onFooterFinish,
  isFinishDisabled,
  children
}) => {
  const activeStepIndex = steps.findIndex((step) => step.id === activeStepId);
  const activeStep = steps[activeStepIndex];
  const showFooterActions = Boolean(
    footerActionsStepId && activeStepId === footerActionsStepId
  );

  const stepHelpers = useMemo(() => ({
    activeStep,
    activeStepIndex,
    onNext: () => {
      const nextIndex = activeStepIndex + 1;

      if (nextIndex < steps.length && onStepChange) {
        onStepChange(steps[nextIndex].id);
      }
    },
    onPrevious: () => {
      const prevIndex = activeStepIndex - 1;

      if (prevIndex >= 0 && onStepChange) {
        onStepChange(steps[prevIndex].id);
      }
    },
    onBack: () => {
      const prevIndex = activeStepIndex - 1;

      if (prevIndex >= 0 && onStepChange) {
        onStepChange(steps[prevIndex].id);
      }
    },
    onSkip: () => {
      if (!activeStep?.optional) {
        return;
      }

      if (onStepComplete) {
        onStepComplete(activeStep.id, { skipped: true });
      }

      const nextIndex = activeStepIndex + 1;

      if (nextIndex < steps.length && onStepChange) {
        onStepChange(steps[nextIndex].id);
      } else if (onBackNavClick) {
        onBackNavClick();
      }
    },
    onComplete: (data) => {
      if (onStepComplete) {
        onStepComplete(activeStep.id, data);
      }
    },
    onClose: onBackNavClick
  }), [
    activeStep,
    activeStepIndex,
    onBackNavClick,
    onStepChange,
    onStepComplete,
    steps
  ]);

  const stepContent = typeof children === 'function'
    ? children(activeStepId, stepHelpers)
    : children;

  const renderFooterActions = () => {
    if (!showFooterActions) {
      return null;
    }

    return (
      <div className={classes.actionsRow}>
        <Button
          type="button"
          className={classes.cancelButton}
          onClick={onFooterCancel || onBackNavClick}
        >
          {cancelButtonLabel}
        </Button>
        <Button
          type="button"
          className={classes.finishButton}
          onClick={onFooterFinish}
          disabled={isFinishDisabled}
        >
          {finishButtonLabel}
        </Button>
      </div>
    );
  };

  return (
    <StepperShellStylesRoot className={classes.root}>
      <DashboardCard
        title={title}
        onBackNavClick={onBackNavClick}
        config={{ showBackNav: true }}
        components={{
          Root: StepperShellDashboardCardRoot,
          Header: StepperShellDashboardCardHeader,
          ActionsLeftContainer: StepperShellDashboardCardActionsLeft,
          ActionsRight: StepperShellDashboardCardActionsRight
        }}
        classes={{
          root: classes.dashboardCardRoot,
          content: classes.dashboardCardContent,
          header: classes.dashboardCardHeader,
          actionsLeftContainer: classes.dashboardCardActionsLeft,
          actionsRight: classes.dashboardCardActionsRight,
          title: classes.dashboardCardTitle
        }}
        actions={{
          right: [{
            type: 'custom',
            element: (
              <QuickScheduleStepper
                steps={steps}
                activeStepId={activeStepId}
                completedStepIds={completedStepIds}
                onStepChange={onStepChange}
              />
            )
          }]
        }}
      >
        {stepContent}
        {renderFooterActions()}
        {disclaimer && (
          <Typography component="p" className={classes.disclaimer}>
            {disclaimer}
          </Typography>
        )}
      </DashboardCard>
    </StepperShellStylesRoot>
  );
};

StepperShell.propTypes = {
  title: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      optional: PropTypes.bool
    })
  ).isRequired,
  activeStepId: PropTypes.string.isRequired,
  completedStepIds: PropTypes.arrayOf(PropTypes.string),
  onStepChange: PropTypes.func,
  onStepComplete: PropTypes.func,
  onBackNavClick: PropTypes.func,
  disclaimer: PropTypes.string,
  footerActionsStepId: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
  finishButtonLabel: PropTypes.string,
  onFooterCancel: PropTypes.func,
  onFooterFinish: PropTypes.func,
  isFinishDisabled: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired
};

StepperShell.defaultProps = {
  completedStepIds: [],
  onStepChange: null,
  onStepComplete: null,
  onBackNavClick: null,
  disclaimer: '',
  footerActionsStepId: null,
  cancelButtonLabel: 'Cancel',
  finishButtonLabel: 'Finish',
  onFooterCancel: null,
  onFooterFinish: null,
  isFinishDisabled: false
};

export default StepperShell;
